import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import { SkeletonBlock } from "../../components/Skeleton/Skeleton";
import { countPatientUnreadChatThreads } from "../../utils/patientChatReadState";
import RequestJourneyPanel from "../../components/RequestJourneyPanel/RequestJourneyPanel";
import { Plus } from "lucide-react";
import "../../HealBidDashboard/HealBidDashboard.scss";

function PatientRequestsSkeleton() {
  return (
    <div className="prPageSkeleton" aria-busy="true" aria-label="Loading">
      <div className="prPage__list">
        <ul className="prPage__ul prPageSkeleton__ul">
          {[0, 1, 2].map((i) => (
            <li key={`pr-skel-${i}`}>
              <div className="prPage__row prPageSkeleton__row">
                <div className="prPageSkeleton__rowMain">
                  <div className="prPage__rowTop prPageSkeleton__rowTop">
                    <SkeletonBlock height={24} width={118} className="prPageSkeleton__pill" />
                    <SkeletonBlock height={14} width={148} />
                  </div>
                  <SkeletonBlock height={18} width="96%" className="prPageSkeleton__line" />
                  <SkeletonBlock height={18} width="78%" className="prPageSkeleton__line" />
                  <SkeletonBlock height={15} width={132} className="prPageSkeleton__meta" />
                </div>
                <div className="prPage__journey prPageSkeleton__journey">
                  <div className="prPageSkeleton__jtHead">
                    <SkeletonBlock height={12} width={72} />
                    <SkeletonBlock height={24} width={96} className="prPageSkeleton__pill prPageSkeleton__pill--sm" />
                  </div>
                  <ol className="prPageSkeleton__steps">
                    {[1, 2, 3, 4].map((step) => (
                      <li key={step} className="prPageSkeleton__step">
                        <SkeletonBlock width={28} height={28} className="prPageSkeleton__orb" />
                        <div className="prPageSkeleton__stepCopy">
                          <SkeletonBlock height={14} width={step % 2 ? "72%" : "58%"} />
                          <SkeletonBlock height={12} width="88%" className="prPageSkeleton__desc" />
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function workflowPillClass(w) {
  const u = String(w || "").toUpperCase();
  if (u === "BIDDING" || u === "BID_SELECTED") return "isOpen";
  if (u === "VERIFIED" || u === "BOOKED" || u === "VISIT_COMPLETED") return "isOk";
  return "isNeutral";
}

function workflowLabel(w) {
  const u = String(w || "").toUpperCase();
  if (u === "BIDDING" || u === "BID_SELECTED") return "Doctors replying";
  if (u === "VERIFIED") return "Verified";
  if (u === "PENDING_ADMIN_REVIEW") return "In review";
  if (u === "DRAFT" || !u) return "Draft";
  if (u === "BOOKED") return "Booked";
  if (u === "VISIT_COMPLETED") return "Visit completed";
  return String(w).replace(/_/g, " ");
}

/**
 * Draft rows are created when the patient starts the wizard; they should not appear in this list.
 * The list API attaches documentVerification to every row (including DRAFT), so never use that
 * alone as a "submitted" signal — same logic as HealBidDashboard.jsx.
 */
function isSubmittedRequest(row) {
  if (row?.isDraft === true) return false;
  const w = String(row?.requestWorkflowStatus || "").toUpperCase();
  if (w === "DRAFT") return false;
  if (row?.submittedForReviewAt) return true;
  if (w) return true;
  return false;
}

function useMatchMediaMinWidth(minPx) {
  const query = `(min-width: ${minPx}px)`;
  const getSnapshot = () =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false;
  const subscribe = (cb) => {
    if (typeof window === "undefined") return () => {};
    const mq = window.matchMedia(query);
    mq.addEventListener("change", cb);
    return () => mq.removeEventListener("change", cb);
  };
  return React.useSyncExternalStore(subscribe, getSnapshot, () => false);
}

function parseTimestamp(value) {
  const ms = new Date(value || "").getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function latestRequestTimestamp(row) {
  return Math.max(
    parseTimestamp(row?.updatedAt),
    parseTimestamp(row?.submittedForReviewAt),
    parseTimestamp(row?.createdAt)
  );
}

function procedurePathFromRequest(row) {
  const pushUnique = (list, value) => {
    const text = String(value || "").trim();
    if (!text) return;
    if (list.length > 0 && list[list.length - 1].toLowerCase() === text.toLowerCase()) return;
    list.push(text);
  };

  const partsFromLabel = (value) => {
    const s = String(value || "").trim();
    if (!s) return [];
    const dotParts = s.split(/\s*·\s*/).map((p) => p.trim()).filter(Boolean);
    if (dotParts.length > 1) return dotParts;
    return s.split(/\s*>\s*/).map((p) => p.trim()).filter(Boolean);
  };

  const candidates = [];

  const expandedParts = [];
  const blocks = Array.isArray(row?.selectionsExpanded) ? row.selectionsExpanded : [];
  for (const block of blocks) {
    pushUnique(expandedParts, block?.categoryName);
    const subs = Array.isArray(block?.subCategories) ? block.subCategories : [];
    for (const sub of subs) {
      pushUnique(expandedParts, sub?.subCategoryName || sub?.name);
    }
  }
  if (expandedParts.length) candidates.push(expandedParts);

  const selectedParts = [];
  const selectedNames = Array.isArray(row?.selectedSubCategoryNames)
    ? row.selectedSubCategoryNames
    : Array.isArray(row?.selectedNames)
      ? row.selectedNames
      : typeof row?.selectedSubCategoryNames === "string"
        ? row.selectedSubCategoryNames.split(",")
        : typeof row?.selectedNames === "string"
          ? row.selectedNames.split(",")
          : [];
  for (const name of selectedNames) pushUnique(selectedParts, name);
  if (selectedParts.length) candidates.push(selectedParts);

  const selectionParts = [];
  const selections = Array.isArray(row?.selections) ? row.selections : [];
  for (const selection of selections) {
    pushUnique(
      selectionParts,
      selection?.categoryName || selection?.category?.name || selection?.category?.categoryName
    );
    const subCategories = Array.isArray(selection?.subCategories)
      ? selection.subCategories
      : Array.isArray(selection?.selectedSubCategories)
        ? selection.selectedSubCategories
        : [];
    for (const sub of subCategories) {
      pushUnique(selectionParts, sub?.subCategoryName || sub?.name);
    }
    const subCategoryIds = Array.isArray(selection?.subCategoryIds) ? selection.subCategoryIds : [];
    for (const sub of subCategoryIds) {
      if (typeof sub === "string") continue;
      pushUnique(selectionParts, sub?.subCategoryName || sub?.name);
    }
    pushUnique(selectionParts, selection?.subCategoryName || selection?.subcategoryName || selection?.name);
  }
  if (selectionParts.length) candidates.push(selectionParts);

  const labelParts = partsFromLabel(row?.categoryLabel);
  if (labelParts.length) candidates.push(labelParts);

  const breadcrumbParts = partsFromLabel(row?.categoryBreadcrumb);
  if (breadcrumbParts.length) candidates.push(breadcrumbParts);

  if (!candidates.length) return "Care";
  const deepest = candidates.reduce((best, current) => (current.length > best.length ? current : best));
  return deepest.join(" > ");
}

export default function PatientRequestsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isWideSplit = useMatchMediaMinWidth(1024);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [rowDetailsById, setRowDetailsById] = useState({});
  const [messageBadge, setMessageBadge] = useState(0);
  const [selectedId, setSelectedId] = useState("");

  const load = useCallback(async () => {
    if (!tokenManager.getTokenForRole("patient")) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const [listRes, convRes] = await Promise.all([
        apiRequest({ method: "GET", url: ENDPOINTS.LIST_MY_MATCH_REQUESTS }),
        apiRequest({ method: "GET", url: ENDPOINTS.GET_CONVERSATIONS, params: { limit: 30 } }).catch(
          () => null
        ),
      ]);
      let list = Array.isArray(listRes?.requests) ? listRes.requests : [];
      list = list.filter(isSubmittedRequest);
      list.sort((a, b) => latestRequestTimestamp(b) - latestRequestTimestamp(a));
      const convItems = Array.isArray(convRes?.items) ? convRes.items : [];
      setMessageBadge(countPatientUnreadChatThreads(convItems));
      setRows(list);
      if (list.length > 0) {
        const detailPairs = await Promise.all(
          list.map(async (row) => {
            const id = String(row?.matchRequestId || "");
            if (!id) return null;
            try {
              const detailRes = await apiRequest({
                method: "GET",
                url: ENDPOINTS.GET_MATCH_REQUEST_DETAIL(id),
              });
              const detailRow = detailRes?.request || null;
              return detailRow ? [id, detailRow] : null;
            } catch {
              return null;
            }
          })
        );
        const nextMap = {};
        for (const pair of detailPairs) {
          if (!pair) continue;
          nextMap[pair[0]] = pair[1];
        }
        setRowDetailsById(nextMap);
      } else {
        setRowDetailsById({});
      }
    } catch (e) {
      setErr(e?.message || "Could not load this list.");
      setRows([]);
      setRowDetailsById({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, pathname]);

  useEffect(() => {
    if (!isWideSplit || !rows.length) return;
    const has = rows.some((r) => String(r.matchRequestId) === selectedId);
    if (!has) setSelectedId(String(rows[0].matchRequestId));
  }, [isWideSplit, rows, selectedId]);

  const selectedRow = useMemo(
    () => rows.find((r) => String(r.matchRequestId) === selectedId) || null,
    [rows, selectedId]
  );
  const selectedDisplay = useMemo(() => {
    if (!selectedRow) return null;
    const id = String(selectedRow.matchRequestId);
    return rowDetailsById[id] ? { ...selectedRow, ...rowDetailsById[id] } : selectedRow;
  }, [selectedRow, rowDetailsById]);

  useEffect(() => {
    const onChatRead = () => {
      void (async () => {
        if (!tokenManager.getTokenForRole("patient")) return;
        try {
          const convRes = await apiRequest({
            method: "GET",
            url: ENDPOINTS.GET_CONVERSATIONS,
            params: { limit: 30 },
          });
          const convItems = Array.isArray(convRes?.items) ? convRes.items : [];
          setMessageBadge(countPatientUnreadChatThreads(convItems));
        } catch (_) {}
      })();
    };
    window.addEventListener("healbid_patient_chat_read", onChatRead);
    return () => window.removeEventListener("healbid_patient_chat_read", onChatRead);
  }, []);

  const onNew = () => {
    navigate("/request/new");
  };

  return (
    <PatientAppLayout activeNav="requests" messageBadgeCount={messageBadge}>
      <div className="hbMain prPage">
        <button
          type="button"
          className="prPage__fab"
          onClick={onNew}
          aria-label="Add new"
        >
          <Plus size={26} strokeWidth={2.25} aria-hidden />
        </button>

        {err ? <p className="hbSub" style={{ color: "#a0342f" }}>{err}</p> : null}

        <div className="prPage__list">
          {loading ? (
            <PatientRequestsSkeleton />
          ) : rows.length === 0 ? (
            <div className="prPage__empty">
              <p className="hbSub">No items yet</p>
            </div>
          ) : isWideSplit ? (
            <div className="prPage__board">
              <div className="prPage__panel prPage__panel--list">
                <ul className="prPage__ul prPage__ul--board">
                  {rows.map((row) => {
                    const id = String(row.matchRequestId);
                    const displayRow = rowDetailsById[id]
                      ? { ...row, ...rowDetailsById[id] }
                      : row;
                    const ts = row.createdAt ? new Date(row.createdAt).toLocaleString() : "—";
                    const label = procedurePathFromRequest(displayRow);
                    const bids = row.providerResponseCount != null ? Number(row.providerResponseCount) : 0;
                    const w = row.requestWorkflowStatus || "";
                    const dv = row.documentVerification;
                    const fileCount =
                      dv?.scope === "linked" && dv?.linkedPrescriptionCount != null
                        ? Number(dv.linkedPrescriptionCount)
                        : null;
                    const active = id === selectedId;
                    return (
                      <li key={id}>
                        <div className="prPage__row">
                          <button
                            type="button"
                            className={`prPage__rowMain${active ? " is-active" : ""}`}
                            aria-current={active ? "true" : undefined}
                            onClick={() => setSelectedId(id)}
                          >
                            <div className="prPage__rowTop">
                              <span className={`prStatusPill prStatusPill--${workflowPillClass(w)}`}>
                                <span className="prStatusPill__dot" aria-hidden />
                                {workflowLabel(w)}
                              </span>
                              <span className="prPage__time">{ts}</span>
                            </div>
                            <div className="hbProcedureSelected hbProcedureSelected--compact">
                              <span className="hbProcedureSelected__label">Selected procedure</span>
                              <div className="prPage__breadcrumb">{label}</div>
                            </div>
                            <div className="prPage__meta">
                              {bids} bid{bids === 1 ? "" : "s"}
                              {fileCount != null ? ` · ${fileCount} file${fileCount === 1 ? "" : "s"}` : null}
                            </div>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {selectedDisplay ? (
                <aside className="prPage__panel prPage__panel--detail" aria-label="Selected request">
                  <div className="prPage__detailHeader">
                    <div className="prPage__detailHeaderTop">
                      <span
                        className={`prStatusPill prStatusPill--${workflowPillClass(
                          selectedDisplay.requestWorkflowStatus
                        )}`}
                      >
                        <span className="prStatusPill__dot" aria-hidden />
                        {workflowLabel(selectedDisplay.requestWorkflowStatus || "")}
                      </span>
                      <span className="prPage__time">
                        {selectedDisplay.createdAt
                          ? new Date(selectedDisplay.createdAt).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                    <p className="prPage__detailProcedure">
                      {procedurePathFromRequest(selectedDisplay)}
                    </p>
                    <p className="prPage__detailMeta">
                      {(() => {
                        const sb =
                          selectedDisplay.providerResponseCount != null
                            ? Number(selectedDisplay.providerResponseCount)
                            : 0;
                        const sdv = selectedDisplay.documentVerification;
                        const sfc =
                          sdv?.scope === "linked" && sdv.linkedPrescriptionCount != null
                            ? Number(sdv.linkedPrescriptionCount)
                            : null;
                        return (
                          <>
                            {sb} bid{sb === 1 ? "" : "s"}
                            {sfc != null ? ` · ${sfc} file${sfc === 1 ? "" : "s"}` : null}
                          </>
                        );
                      })()}
                    </p>
                  </div>
                  <div className="prPage__detailBody">
                    <div className="prPage__journey prPage__journey--detail">
                      <RequestJourneyPanel
                        layout="embedded"
                        matchRequestId={String(selectedDisplay.matchRequestId)}
                        workflow={selectedDisplay.requestWorkflowStatus}
                        requestRow={selectedDisplay}
                      />
                    </div>
                  </div>
                  <div className="prPage__detailFooter">
                    <button
                      type="button"
                      className="hbActionBtn prPage__openFullBtn"
                      onClick={() =>
                        navigate(
                          `/patient-match-request/${encodeURIComponent(String(selectedDisplay.matchRequestId))}`,
                          { state: { requestRow: selectedDisplay } }
                        )
                      }
                    >
                      Open request
                    </button>
                  </div>
                </aside>
              ) : null}
            </div>
          ) : (
            <ul className="prPage__ul">
              {rows.map((row) => {
                const id = String(row.matchRequestId);
                const displayRow = rowDetailsById[id] ? { ...row, ...rowDetailsById[id] } : row;
                const ts = row.createdAt ? new Date(row.createdAt).toLocaleString() : "—";
                const label = procedurePathFromRequest(displayRow);
                const bids = row.providerResponseCount != null ? Number(row.providerResponseCount) : 0;
                const w = row.requestWorkflowStatus || "";
                const dv = row.documentVerification;
                const fileCount =
                  dv?.scope === "linked" && dv?.linkedPrescriptionCount != null
                    ? Number(dv.linkedPrescriptionCount)
                    : null;
                return (
                  <li key={id}>
                    <div className="prPage__row">
                      <button
                        type="button"
                        className="prPage__rowMain"
                        onClick={() =>
                          navigate(`/patient-match-request/${encodeURIComponent(id)}`, {
                            state: { requestRow: row },
                          })
                        }
                      >
                        <div className="prPage__rowTop">
                          <span className={`prStatusPill prStatusPill--${workflowPillClass(w)}`}>
                            <span className="prStatusPill__dot" aria-hidden />
                            {workflowLabel(w)}
                          </span>
                          <span className="prPage__time">{ts}</span>
                        </div>
                        <div className="hbProcedureSelected hbProcedureSelected--compact">
                          <span className="hbProcedureSelected__label">Selected procedure</span>
                          <div className="prPage__breadcrumb">{label}</div>
                        </div>
                        <div className="prPage__meta">
                          {bids} bid{bids === 1 ? "" : "s"}
                          {fileCount != null ? ` · ${fileCount} file${fileCount === 1 ? "" : "s"}` : null}
                        </div>
                      </button>
                      <div className="prPage__journey">
                        <RequestJourneyPanel
                          layout="embedded"
                          matchRequestId={id}
                          workflow={displayRow.requestWorkflowStatus}
                          requestRow={displayRow}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </PatientAppLayout>
  );
}
